import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { anonymiserAdresse, anonymiserSti } from './telemetristier';

/**
 * Nettsiden sin halvdel av Application Insights.
 *
 * Målet er at en feil i nettleseren og en feil på serveren havner samme sted,
 * i samme tidslinje, med samme operasjons-id – så man ser at den hvite skjermen
 * hos brudeparet klokka ti hører sammen med unntaket i API-et klokka ti.
 *
 * Tre valg er verdt å forklare:
 *
 * **SDK-et lastes dynamisk.** Det er drøye hundre kilobyte, og det trengs bare
 * når det finnes et sted å sende til. Lokalt og i testene svarer `/api/telemetri`
 * at overvåkinga er av, og da hentes pakken aldri – den havner i sin egen bit
 * fra Vite og belaster ikke førstelasten for noen.
 *
 * **Ingen informasjonskapsler.** Application Insights setter til vanlig `ai_user`
 * og `ai_session` for å kjenne igjen den samme nettleseren over tid. Vår Dag
 * setter bare kapsler som er nødvendige for at tjenesten skal virke, og sier det
 * rett ut i beskjeden nederst på sida – en analysekapsel ville gjort den
 * beskjeden usann og krevd et samtykke vi ikke ber om. Uten dem er hver
 * sidelasting anonym for seg, og det er nok til å se at noe feiler.
 *
 * **Adressene anonymiseres før de sendes.** `enableAutoRouteTracking` ville ellers
 * sendt `/rsvp/<polett>` som navnet på sidevisningen, og den poletten er en
 * gjests innlogging.
 */

type Oppsett = {
  aktiv: boolean;
  tilkoblingsstreng: string;
  rolle: string;
  versjon: string;
};

let klient: ApplicationInsights | null = null;
let startet = false;

/**
 * Feil som ble meldt før SDK-et rakk å bli lastet.
 *
 * Oppkoblinga er asynkron: den henter oppsettet fra serveren og laster deretter
 * pakken. Den viktigste feilen av alle – den som velter første rendring – skjer
 * *før* det er ferdig. Målt i nettleseren: uten denne køen viste feilgrensa
 * beskjeden sin helt riktig, mens det ikke gikk ett eneste unntak ut.
 *
 * Køen har en øvre grense. Blir overvåkinga aldri koblet opp, skal en side som
 * feiler i en løkke ikke spise minnet i tillegg.
 */
const KOELENGDE = 20;
let koe: Array<{ feil: unknown; kontekst?: Record<string, string> }> = [];

/**
 * Kobler opp mot Application Insights, dersom serveren sier at den er satt opp.
 *
 * Kalles én gang ved oppstart. Feiler noe – nettverket, oppsettet, pakken –
 * skal siden virke like fullt: overvåking som tar ned tjenesten den overvåker,
 * er verre enn ingen overvåking.
 */
export async function startTelemetri(): Promise<void> {
  if (startet) return;
  startet = true;

  try {
    const svar = await fetch('/api/telemetri', { credentials: 'omit' });
    if (!svar.ok) return glemKoen();

    const oppsett = (await svar.json()) as Oppsett;
    if (!oppsett.aktiv || !oppsett.tilkoblingsstreng) return glemKoen();

    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');

    const ai = new ApplicationInsights({
      config: {
        connectionString: oppsett.tilkoblingsstreng,

        // Sidevisninger i en enkeltsideapp: uten denne teller bare den aller
        // første lastinga, og alt brukeren gjør etterpå er usynlig.
        enableAutoRouteTracking: true,

        // Ubehandlede feil og avviste løfter er hele poenget med å være her.
        disableExceptionTracking: false,
        enableUnhandledPromiseRejectionTracking: true,

        // Kall til vårt eget API blir avhengigheter, og knyttes til
        // serversporet gjennom `traceparent`-hodet. Det er denne koblinga som
        // gjør at man kan klikke seg fra en treg side til spørringen som var treg.
        disableAjaxTracking: false,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,

        // Se toppkommentaren: ingen kapsler uten at de er nødvendige.
        disableCookiesUsage: true,

        // Send raskt. Trafikken er liten, og en feil som ligger i køen når
        // fanen lukkes, er en feil vi aldri får høre om.
        maxBatchInterval: 5000,

        /**
         * Stopper SDK-et fra å hente oppsettet sitt fra Microsofts CDN.
         *
         * Som standard spør det `js.monitor.azure.com` om et JSON-oppsett med
         * jevne mellomrom. Det er en utgående forbindelse vi verken trenger
         * eller vil ha: innholdssikkerhetspolicyen slipper bare gjennom selve
         * ingestion-adressen, og et SDK som skal melde fra om feil, skal ikke
         * hente nye innstillinger fra nettet mens det gjør det.
         *
         * Målt i nettleseren: uten dette står det to CSP-brudd i konsollen på
         * hver eneste sidelasting.
         */
        extensionConfig: {
          AppInsightsCfgSyncPlugin: { cfgUrl: '', blkCdnCfg: true },
        },
      },
    });

    ai.loadAppInsights();

    /**
     * Siste sperre før noe forlater nettleseren.
     *
     * Alt som har en adresse i seg vaskes her – sidevisninger, avhengigheter,
     * unntak. Å legge det på ett sted i stedet for på hvert kallested er med
     * vilje: den neste som legger til en `trackEvent` skal ikke trenge å huske
     * det.
     */
    ai.addTelemetryInitializer((element) => {
      /**
       * Operasjonsnavnet rettes ved kilden, ikke i resultatet.
       *
       * `ai.operation.name` settes ikke her, men av tilleggsmodulen som kjører
       * *etter* alle initialisatorene, og den leser navnet fra
       * `telemetryTrace.name`. Skrev vi til `element.tags` i stedet, ville det
       * blitt overskrevet igjen rett etterpå – målt i nettleseren: da sto hele
       * svarlenka i klartekst i `ai.operation.name`, mens `uri` var pen.
       *
       * `enableAutoRouteTracking` setter det samme feltet på nytt ved hver
       * navigering, så det må rettes for hvert element, ikke bare én gang ved
       * oppstart.
       */
      const spor = ai.context?.telemetryTrace;
      if (spor) spor.name = anonymiserSti(window.location.pathname);

      const data = element.baseData;
      if (data) {
        /**
         * Uten denne linja går svarlenka ut i klartekst. Målt: med
         * sanitiseringa av sto hele `/rsvp/<polett>` i `url`-feltet på hver
         * eneste sidevisning.
         *
         * SDK-et kjører sin egen vask av `url` etterpå, og den er grov – den
         * korter ned det vi sender inn hit til `http://{id}`. Det er kosmetisk
         * og uten personopplysninger; feltet Application Insights faktisk
         * grupperer på er `ai.operation.name`, og det settes riktig lenger nede.
         */
        if (typeof data['uri'] === 'string') data['uri'] = anonymiserSti(data['uri']);
        // Tom henvisning skal bli stående tom. `anonymiserSti('')` svarer `/`,
        // og «kom fra forsida» er noe annet enn «kom ikke fra noe sted».
        if (typeof data['refUri'] === 'string' && data['refUri'] !== '') {
          data['refUri'] = anonymiserSti(data['refUri']);
        }

        /**
         * Avhengigheter: kall fra sida til vårt eget API.
         *
         * `name` og `data` er begge på formen `GET /api/rsvp/<polett>`. Målt i
         * nettleseren: uten disse linjene gikk svarlenka ut i klartekst hver
         * gang RSVP-sida hentet noe.
         *
         * Det skilles på type, og det er nødvendig: på en sidevisning er `name`
         * *sidetittelen*, ikke en adresse. «Vår Dag – bryllupsplanlegging
         * gjort enkelt» er lengre enn polettgrensa, så en felles behandling
         * ville gjort hver eneste sidetittel om til `{id}`.
         */
        if (element.baseType === 'RemoteDependencyData') {
          if (typeof data['name'] === 'string') data['name'] = anonymiserAdresse(data['name']);
          if (typeof data['data'] === 'string') data['data'] = anonymiserAdresse(data['data']);
        } else if (typeof data['name'] === 'string' && data['name'].startsWith('/')) {
          data['name'] = anonymiserSti(data['name']);
        }
      }

      // Rollenavnet skiller nettsiden fra API-et i applikasjonskartet.
      // `tags` kommer som en tom tabell fra SDK-et, ikke som et objekt, så
      // `?? {}` ville ikke slått til – begge deler tar imot en nøkkel.
      element.tags = element.tags ?? {};
      element.tags['ai.cloud.role'] = oppsett.rolle;
      element.tags['ai.application.ver'] = oppsett.versjon;
      element.tags['ai.operation.name'] = anonymiserSti(window.location.pathname);
      return true;
    });

    /**
     * Den første sidevisningen må sendes selv, og først *nå*.
     *
     * `enableAutoRouteTracking` fanger bare *senere* navigeringer i
     * enkeltsideappen. Uten dette kallet er den første sida – som er den de
     * fleste ser – usynlig, og ingen sesjon kommer i gang. Målt i nettleseren:
     * uten det gikk det ikke ett eneste beacon ut.
     *
     * Den står under registreringa av initialisatoren, ikke over: den som
     * vasker adressene må være på plass før det første elementet lages.
     */
    ai.trackPageView();

    klient = ai;

    // Alt som kom inn mens vi lastet, sendes nå.
    for (const ventende of koe) sporFeil(ventende.feil, ventende.kontekst);
    koe = [];
  } catch {
    // Med vilje tom: siden skal virke uten overvåking.
    glemKoen();
  }
}

/** Ingen kommer til å hente disse. Da skal de heller ikke ligge og oppta plass. */
function glemKoen(): void {
  koe = [];
}

/** Melder fra om en feil vi fanget selv, for eksempel i feilgrensa. */
export function sporFeil(feil: unknown, kontekst?: Record<string, string>): void {
  try {
    if (!klient) {
      // Ennå ikke koblet opp. Legg den i kø – enten sendes den om et øyeblikk,
      // eller så er overvåkinga av, og da kastes køen.
      if (koe.length < KOELENGDE) koe.push({ feil, kontekst });
      return;
    }
    klient.trackException({
      exception: feil instanceof Error ? feil : new Error(String(feil)),
      properties: kontekst,
    });
  } catch {
    // Med vilje tom.
  }
}

/** Om overvåkinga er koblet opp. Finnes for testene og for feilsøking. */
export function telemetriPaa(): boolean {
  return klient !== null;
}
